import React from 'react';
import { type Player, Sex } from '@iziping/common';
import styles from './PlayerCard.module.css';
import { useNavigate } from 'react-router-dom';
import { Building2, Hash, Tag, Pencil } from 'lucide-react';

interface PlayerCardProps {
    player: Player;
    style?: React.CSSProperties;
}

const AGE_LABEL: Record<string, string> = {
    SENIOR: 'Senior',
    VETERAN: 'Vétéran',
    JUNIOR: 'Junior',
    CADET: 'Cadet',
    MINIME: 'Minime',
    BENJAMIN: 'Benjamin',
    POUSSIN: 'Poussin',
};

const PlayerCard: React.FC<PlayerCardProps> = ({ player, style }) => {
    const navigate = useNavigate();

    if (!player) return null;

    const initials = `${player.firstname?.charAt(0) ?? ''}${player.lastname?.charAt(0) ?? ''}`.toUpperCase();
    const isMale = player.sex === Sex.MASCULIN;
    const ageLabel = AGE_LABEL[player.age] ?? player.age ?? 'Non renseigné';

    return (
        <div className={`${styles.card} ${isMale ? styles.male : styles.female}`} style={style}>
            <div className={styles.top}>
                <div className={styles.avatar} aria-hidden="true">
                    {initials}
                </div>
                <div className={styles.nameBlock}>
                    <button
                        className={styles.nameBtn}
                        onClick={() => navigate(`/players/${player.id || 0}`)}
                        aria-label={`Modifier la fiche de ${player.firstname} ${player.lastname}`}
                    >
                        <span className={styles.firstname}>{player.firstname}</span>
                        <span className={styles.lastname}>
                            {player.lastname.toUpperCase()}
                            <Pencil size={12} className={styles.pencil} aria-hidden="true" />
                        </span>
                    </button>
                </div>
            </div>

            <div className={styles.pointsBlock}>
                <span className={styles.pointsNumber}>{player.points ?? '—'}</span>
                <span className={styles.pointsUnit}>pts</span>
            </div>

            <div className={styles.divider} />

            <div className={styles.infos}>
                <div className={styles.infoGrid}>
                    <div className={styles.infoChip}>
                        <span className={styles.infoChipLabel}>
                            <Building2 size={11} aria-hidden="true" />
                            Club
                        </span>
                        <span className={styles.infoChipValue}>{player.club?.name || 'Sans club'}</span>
                    </div>
                    <div className={styles.infoChip}>
                        <span className={styles.infoChipLabel}>
                            <Tag size={11} aria-hidden="true" />
                            Catégorie
                        </span>
                        <span className={styles.infoChipValue}>{ageLabel}</span>
                    </div>
                </div>
                <div className={styles.infoChipWide}>
                    <span className={styles.infoChipLabel}>
                        <Hash size={11} aria-hidden="true" />
                        Licence
                    </span>
                    <span className={styles.infoChipValue}>{player.licence}</span>
                </div>
            </div>
        </div>
    );
};

export default PlayerCard;
